import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * URL-state-as-truth: a tiny `useState`-like API backed by query string params.
 * Default values are *omitted* from the URL to keep it clean.
 *
 *   const [q, setQ] = useUrlState("q", "");
 *   const [severity, setSeverity] = useUrlState<"high"|"low"|"all">("sev", "all");
 */
export function useUrlState<T extends string>(
  key: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [params, setParams] = useSearchParams();
  const value = (params.get(key) ?? defaultValue) as T;

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setParams(
        (prev) => {
          const merged = new URLSearchParams(prev);
          const current = (merged.get(key) ?? defaultValue) as T;
          const resolved = typeof next === "function" ? (next as (p: T) => T)(current) : next;
          if (resolved === defaultValue || resolved === "" || resolved == null) {
            merged.delete(key);
          } else {
            merged.set(key, String(resolved));
          }
          return merged;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setParams],
  );

  return useMemo(() => [value, setValue], [value, setValue]);
}
